"use client"

import { useTranslations } from "@/lib/intl/use-translations"
import { CoverageChecker } from "@/components/insurance/coverage-checker"
import {
  InsuranceClosingCta,
  InsuranceJourneySection,
  InsurancePhotoHero,
  InsuranceTypesSection,
  SubtitleWithTermsLink,
} from "@/components/insurance/insurance-page-sections"
import type { InsurerData } from "@/lib/insurance/insurance-types"
import { insuranceKeys as k } from "@/i18n/insurance-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { notFound } from "next/navigation"
import { FaqSection } from "@/components/ui/faq-section"
import { InsurerExamples } from "@/components/ui/insurer-examples"

/* ───────────────────── Page ───────────────────── */

export default function InsurancePageClient({
  initialFaqs,
  initialInsurerData,
}: {
  initialFaqs: { question: string; answer: string }[]
  initialInsurerData?: InsurerData | null
}) {
  const { code: country } = useCountry()
  // Insurance page is CH-only (Swiss insurers). NL visitors see 404.
  if (country === "nl") notFound()

  const t = useTranslations("insurance")
  const tp = useTranslations("insurancePages")
  const lp = useLocalePath()

  const JOURNEY_STEPS = [
    { title: t(k.step1Title), description: t(k.step1Description) },
    { title: t(k.step2Title), description: t(k.step2Description) },
    { title: t(k.step3Title), description: t(k.step3Description) },
    { title: t(k.step4Title), description: t(k.step4Description) },
  ]

  return (
    <>
      <InsurancePhotoHero
        label={t(k.heroLabel)}
        heading={t(k.heroHeading)}
        subtitle={
          <SubtitleWithTermsLink
            text={t(k.heroSubtitle)}
            linkLabel={tp("termsLink")}
            href={lp("/insurance-checker-terms")}
          />
        }
      />

      {/* Coverage checker embed — pulled up over the hero band. */}
      <section
        className="relative z-10 -mt-32 scroll-mt-24 pb-16 md:-mt-[200px] md:scroll-mt-28"
        id="coverage-form"
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <CoverageChecker data={initialInsurerData ?? null} tone="tinted" />
        </div>
      </section>

      <InsuranceJourneySection
        heading={t(k.howItWorksHeading)}
        steps={JOURNEY_STEPS}
      />

      <InsuranceTypesSection
        heading={t(k.insuranceTypesCoverageHeading)}
        basicTitle={t(k.basicInsuranceCoverageTitle)}
        basicDescription={t(k.basicInsuranceCoverageDescription)}
        basicBadge={t(k.basicInsuranceCoverageBadge)}
        supplementaryTitle={t(k.supplementaryInsuranceCoverageTitle)}
        supplementaryDescription={t(
          k.supplementaryInsuranceCoverageDescription
        )}
        supplementaryBadge={t(k.supplementaryInsuranceCoverageBadge)}
      />

      {/* Real-world examples — also the hub for the per-insurer pages */}
      <InsurerExamples
        coveragePageCopy
        insurerData={initialInsurerData ?? null}
      />

      {/* FAQ */}
      <FaqSection
        heading={t(k.faqCoverageHeading)}
        linkLabel={t(k.readAllFaqs)}
        linkHref={lp("/faq")}
        items={initialFaqs}
        currentPage="insurance"
        layout="stacked"
      />

      <InsuranceClosingCta
        heading={t(k.ctaHeading)}
        subtitle={t(k.ctaSubtitle)}
        primaryLabel={t(k.bookCheckup)}
        primaryHref={lp("/pricing")}
        secondaryLabel={t(k.talkToTeam)}
        secondaryHref={lp("/contact")}
      />
    </>
  )
}
